import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { List, X, ArrowRight } from "@phosphor-icons/react";
import Logo from "../Logo";
import { useLang } from "../../i18n/LanguageContext";

const Header = () => {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/", label: t.nav.home, testid: "nav-home" },
    { to: "/about", label: t.nav.about, testid: "nav-about" },
    { to: "/products", label: t.nav.products, testid: "nav-products" },
    { to: "/industries", label: t.nav.industries, testid: "nav-industries" },
    { to: "/sustainability", label: t.nav.sustainability, testid: "nav-sustainability" },
    { to: "/news", label: t.nav.news, testid: "nav-news" },
    { to: "/careers", label: t.nav.careers, testid: "nav-careers" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur border-b border-neutral-200" : "bg-white border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" className="shrink-0">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.testid}
              className={({ isActive }) =>
                `text-[13px] font-semibold tracking-wide transition-colors ${
                  isActive ? "text-[#C8102E]" : "text-neutral-700 hover:text-[#C8102E]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            className="hidden sm:flex items-center border border-neutral-300 text-[11px] font-bold"
            data-testid="lang-switcher"
          >
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-2.5 py-1 transition-colors ${
                lang === "en" ? "bg-[#171717] text-white" : "text-neutral-700 hover:bg-neutral-100"
              }`}
            >
              EN
            </button>
            <button
              data-testid="lang-fr"
              onClick={() => setLang("fr")}
              className={`px-2.5 py-1 transition-colors ${
                lang === "fr" ? "bg-[#171717] text-white" : "text-neutral-700 hover:bg-neutral-100"
              }`}
            >
              FR
            </button>
          </div>

          <Link
            to="/contact"
            data-testid="header-cta-contact"
            className="hidden sm:inline-flex items-center gap-2 bg-[#C8102E] hover:bg-[#A00D25] text-white px-4 py-2.5 text-[13px] font-semibold transition-colors"
          >
            {t.nav.contact}
            <ArrowRight size={14} weight="bold" />
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-neutral-800"
          >
            {open ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-neutral-200 bg-white" data-testid="mobile-menu">
          <div className="px-5 py-5 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-${l.testid}`}
                className={({ isActive }) =>
                  `py-2.5 text-[15px] font-semibold border-b border-neutral-100 ${
                    isActive ? "text-[#C8102E]" : "text-neutral-800"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-cta-contact"
              className="mt-4 inline-flex items-center justify-center gap-2 bg-[#C8102E] text-white px-4 py-3 font-semibold"
            >
              {t.nav.contact}
              <ArrowRight size={16} weight="bold" />
            </Link>
            <div className="flex items-center gap-2 mt-4 text-[11px] font-bold">
              <button
                onClick={() => setLang("en")}
                data-testid="mobile-lang-en"
                className={`px-3 py-1.5 border border-neutral-300 ${lang === "en" ? "bg-neutral-900 text-white" : ""}`}
              >EN</button>
              <button
                onClick={() => setLang("fr")}
                data-testid="mobile-lang-fr"
                className={`px-3 py-1.5 border border-neutral-300 ${lang === "fr" ? "bg-neutral-900 text-white" : ""}`}
              >FR</button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
